import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

/**
 * Server-synchronized idle timeout (June 2026).
 *
 * The SERVER owns the idle policy: the session's idle timer resets only
 * via the explicit heartbeat below — plain API traffic never does.
 * This hook mirrors it client-side:
 *  - genuine user activity (mouse/key/scroll/touch) is tracked locally;
 *  - a heartbeat is POSTed at most every 2 minutes, and only when real
 *    activity happened since the previous one;
 *  - at WARN_BEFORE before the timeout a warning dialog is surfaced
 *    (passive activity no longer counts — the user must click
 *    "Stay signed in");
 *  - at the timeout, the session is terminated and we return to /login.
 */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const WARN_BEFORE_MS = 2 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;
const EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

export const useIdleTimeout = () => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const lastHeartbeatRef = useRef(Date.now());
  const warningRef = useRef(false);
  const loggingOutRef = useRef(false);

  const staySignedIn = useCallback(async () => {
    try {
      await api.post('/auth/sessions/heartbeat');
    } catch {
      /* if the session already died server-side, the next API 401 redirects */
    }
    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = Date.now();
    warningRef.current = false;
    setShowWarning(false);
  }, []);

  useEffect(() => {
    if (!user) return undefined;

    const markActivity = () => {
      // Once the warning is up, passive activity no longer counts.
      if (!warningRef.current) lastActivityRef.current = Date.now();
    };
    EVENTS.forEach((e) => document.addEventListener(e, markActivity, { passive: true }));

    const tick = setInterval(async () => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= IDLE_TIMEOUT_MS) {
        if (!loggingOutRef.current) {
          loggingOutRef.current = true;
          await logout();
          window.location.href = '/login?reason=idle';
        }
        return;
      }

      if (idle >= IDLE_TIMEOUT_MS - WARN_BEFORE_MS) {
        warningRef.current = true;
        setShowWarning(true);
        setSecondsLeft(Math.max(0, Math.round((IDLE_TIMEOUT_MS - idle) / 1000)));
        return;
      }

      // Heartbeat: keep the SERVER idle timer alive while genuinely active.
      if (
        Date.now() - lastHeartbeatRef.current >= HEARTBEAT_INTERVAL_MS &&
        lastActivityRef.current > lastHeartbeatRef.current
      ) {
        lastHeartbeatRef.current = Date.now();
        api.post('/auth/sessions/heartbeat').catch(() => {});
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      EVENTS.forEach((e) => document.removeEventListener(e, markActivity));
    };
  }, [user, logout]);

  return { showWarning, secondsLeft, staySignedIn };
};
